import { createFileRoute } from "@tanstack/react-router";

const DESCRIPTION =
  "Blanco Pro Services is committed to a website everyone can use. Read our accessibility statement, the standards we follow, and how to reach us for help.";

export const Route = createFileRoute("/accessibility")({
  head: () => ({
    meta: [
      { title: "Accessibility Statement | Blanco Pro Services" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Accessibility Statement | Blanco Pro Services" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://blancopros.com/accessibility" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Accessibility Statement | Blanco Pro Services" },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://blancopros.com/accessibility" }],
  }),
  component: AccessibilityPage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="font-display text-2xl md:text-3xl font-bold mb-4 text-foreground">{title}</h2>
      <div className="space-y-4 text-foreground/80 leading-relaxed">{children}</div>
    </section>
  );
}

function List({ items }: { items: string[] }) {
  return (
    <ul className="list-disc pl-6 space-y-2">
      {items.map((i) => (
        <li key={i}>{i}</li>
      ))}
    </ul>
  );
}

function AccessibilityPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <header className="mb-14 border-b border-foreground/10 pb-8">
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3 font-display">Legal</p>
          <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] mb-4">Accessibility Statement</h1>
          <p className="text-foreground/60 text-sm">Blanco Pro Services LLC · Last Updated: October 29, 2026</p>
        </header>

        <div className="rounded-2xl border border-gold/30 bg-gold/[0.06] p-6 mb-12 text-foreground/85 leading-relaxed">
          Blanco Pro Services LLC is committed to making blancopros.com usable by everyone, including people with
          disabilities. If anything on this site is hard to use, call us at{" "}
          <a href="tel:+19088956133" className="underline underline-offset-4 hover:text-gold">(908) 895-6133</a> or email{" "}
          <a href="mailto:service@blancopros.com" className="underline underline-offset-4 hover:text-gold">service@blancopros.com</a>{" "}
          and we will help you directly.
        </div>

        <Section title="1. Our Commitment">
          <p>
            We believe every homeowner and business owner in New Jersey, Pennsylvania, and New York should be able to learn
            about our services, browse our work, and request an estimate without barriers. We continually review and
            improve our website so it is easier to use for people of all abilities.
          </p>
        </Section>

        <Section title="2. Standards We Follow">
          <p>
            We aim to meet the Web Content Accessibility Guidelines (WCAG) 2.2, Level AA, published by the World Wide Web
            Consortium (W3C). These guidelines explain how to make web content more accessible for people with visual,
            hearing, motor, cognitive, and other disabilities.
          </p>
          <p>
            We consider this website partially conformant with WCAG 2.2 Level AA, meaning some parts of the content may
            not yet fully meet the standard. We are actively working to close those gaps.
          </p>
        </Section>

        <Section title="3. What We Have Done">
          <List
            items={[
              "Built pages with clear headings and page structure so screen readers can navigate them.",
              "Made menus, buttons, and links usable with a keyboard alone.",
              "Added descriptive text to meaningful images.",
              "Used readable text sizes and strong color contrast across the site.",
              "Reduced animation for visitors whose device is set to reduce motion.",
              "Made pages work on phones, tablets, and computers, and at higher zoom levels.",
            ]}
          />
        </Section>

        <Section title="4. Built-In Accessibility Tools">
          <p>
            Every page includes an accessibility menu, opened with the accessibility button at the bottom left of the
            screen or with Ctrl + U on your keyboard. It lets you adjust the site to your needs, including:
          </p>
          <List
            items={[
              "One-click profiles for motor, vision, color blindness, dyslexia, cognitive, seizure, and ADHD needs.",
              "Larger text, extra letter and line spacing, and a dyslexia-friendly font.",
              "High contrast and color saturation adjustments.",
              "Highlighted links and headings, a reading guide, and a larger cursor.",
              "Read aloud, and the option to pause animations.",
            ]}
          />
          <p>Your choices are saved on your device so they stay in place as you browse.</p>
        </Section>

        <Section title="5. Known Limitations">
          <p>
            Some content on this site comes from outside providers, such as our estimate request form, chat window, and
            embedded maps or videos. We choose providers that care about accessibility, but we cannot fully control how
            their content works. If you have trouble with any of these, contact us and we will take your request by phone
            or email instead.
          </p>
        </Section>

        <Section title="6. Feedback and Help">
          <p>
            We welcome your feedback. If you run into a barrier, need information in a different format, or would like
            help requesting an estimate, please reach out:
          </p>
          <List
            items={[
              "Phone: (908) 895-6133",
              "Email: service@blancopros.com",
              "Hours: Monday to Saturday, 7:30 AM to 4:30 PM",
            ]}
          />
          <p>
            Please tell us the page you were on and the problem you ran into. We aim to respond within 2 business days.
          </p>
        </Section>

        <Section title="7. Ongoing Improvements">
          <p>
            Accessibility is an ongoing effort. We review this website regularly, fix issues as we find them, and update
            this statement as we make improvements.
          </p>
        </Section>
      </div>
    </div>
  );
}
